import React, { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import DelayInput from "../DelayInput";
import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";

import { V2 } from "../../Api";
import { formatDateLocal, formatUSD } from "../../Utilities";
import withTitle from "../withTitle";

interface IStatementsListViewState {
    isLoading: boolean | string;

    sortColumn: string;
    sortDirection: string;

    searchOffset: number;
    searchText: string;
    searchStatus: string;

    statements: any[];
}

interface IStatementsListViewProps extends RouteComponentPropsHistory<any, any> {

}

const PAGE_SIZE = 50;

class StatementsListView extends React.Component<IStatementsListViewProps, IStatementsListViewState> {
    constructor(props: Readonly<IStatementsListViewProps>) {
        super(props);

        const state: IStatementsListViewState = {
            isLoading: true,

            sortColumn: "last_update",
            sortDirection: "desc",
            searchOffset: 0,
            searchText: "",
            searchStatus: "",

            statements: []
        };

        if (props.location.state) {
            const pState = props.location.state;

            state.sortColumn = pState.sortColumn || "last_update";
            state.sortDirection = pState.sortDirection || "desc";
            state.searchOffset = pState.searchOffset || 0;
            state.searchText = pState.searchText || "";
            state.searchStatus = pState.searchStatus || "";
        }

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.loadData = this.loadData.bind(this);
        this.sortIcon = this.sortIcon.bind(this);
        this.sortByField = this.sortByField.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.openPdf = this.openPdf.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="stmt:print">
                            <Link to="/accounting/statements/printing" className="text-with-icon" title="Print non-emailable statements"><i className="icon-print" /><span>Statement printing...</span></Link>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <div className="filter-control-box">
                    <DelayInput type="search" timeout={500} className="fill-space" name="searchText" value={this.state.searchText} placeholder="Search..." onChangeDelayed={this.handleElementChange} />
                    <select name="searchStatus" value={this.state.searchStatus} onChange={this.handleElementChange}>
                        <option value="">All</option>
                        <option value="notsent">Not Sent/Not Printed</option>
                        <option value="sent">Sent</option>
                        <option value="printqueued">Print Queued</option>
                        <option value="printed">Printed</option>
                        <option value="skipped">Skipped</option>
                        <option value="paymentauthorized">Payment Authorized</option>
                        <option value="paymentsettled">Payment Settled</option>
                        <option value="paymentrejected">Payment Rejected</option>
                        <option value="paymentdisputed">Payment Disputed</option>
                        <option value="paymentposted">Payment Posted</option>
                        <option value="voided">Voided</option>
                    </select>
                </div>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="clickable col-smallest" onClick={() => this.sortByField("account")}>Account # {this.sortIcon("account")}</th>
                            <th className="clickable col-smallest" onClick={() => this.sortByField("statement_id")}>Statement # {this.sortIcon("statement_id")}</th>
                            <th className="clickable" onClick={() => this.sortByField("name")}>Name {this.sortIcon("name")}</th>
                            <th className="clickable" onClick={() => this.sortByField("last_update")}>Last Update {this.sortIcon("last_update")}</th>
                            <th className="clickable" onClick={() => this.sortByField("amount")}>$ Due {this.sortIcon("amount")}</th>
                            <th className="clickable" onClick={() => this.sortByField("amount_paid")}>$ Paid {this.sortIcon("amount_paid")}</th>
                            <th className="clickable col-smallest" onClick={() => this.sortByField("status")}>Status {this.sortIcon("status")}</th>
                            <th className="col-smallest" title="Can email statements?"><i className="icon-envelope" /></th>
                            <th className="col-smallest" title="Has PDF?"><i className="icon-file-pdf-o" /></th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Statements</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            this.state.isLoading === true
                                ? <tr><td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td></tr>
                                : typeof this.state.isLoading === "string"
                                    ? <tr><td colSpan={10}><span className="error-text">{this.state.isLoading}</span></td></tr>
                                    : this.state.statements.length === 0
                                        ? <tr><td colSpan={10}>No results found</td></tr>
                                        : this.state.statements.map((a, i) => (
                                            <tr className="clickable" key={a.id} tabIndex={0} onClick={() => this.goAccount(a.accountId)}>
                                                <td className="inline col-center" data-caption="Account: ">{a.externalAccountId}</td>
                                                <td className="inline col-center" data-caption="Account: ">{a.externalStatementId}</td>
                                                <td className="inline" data-caption="Name: ">{a.name}</td>
                                                <td className="inline" data-caption="Last Update: ">{formatDateLocal(a.lastUpdate) || "-"}</td>
                                                <td className="inline col-right-vt col-smallest" data-caption="Amount due: ">{formatUSD(a.amount)}</td>
                                                <td className="inline col-right-vt col-smallest" data-caption="Amount paid: ">{formatUSD(a.amountPaid)}</td>
                                                <td className="inline" data-caption="Status: ">{this.statementStatusToText(a.status, a.canEmailStatements) || "-"}</td>
                                                <td className="inline col-center" data-caption="Can email?: ">{a.canEmailStatements ? <i className="icon-envelope" title="Yes, can email" /> : null}</td>
                                                <td className="inline col-center" data-caption="Has PDF?: ">{a.hasPdf ? <i className="icon-file-pdf-o" style={{ color: "#00f" }} title="View PDF" onClick={e => { e.preventDefault(); e.stopPropagation(); this.openPdf(a.id) }} /> : null}</td>
                                            </tr>
                                        ))}
                    </tbody>
                </table>
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.statements.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.loadData());
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters = [
                `offset=${this.state.searchOffset}`,
                `limit=${PAGE_SIZE}`,
            ];

            if (this.state.sortColumn) {
                parameters.push(`sort=${this.state.sortColumn}`);
                parameters.push(`dir=${this.state.sortDirection || "asc"}`);
            }

            if (this.state.searchText) {
                parameters.push(`q=${encodeURIComponent(this.state.searchText)}`);
            }

            if (this.state.searchStatus && !this.state.searchText) {
                parameters.push(`status=${encodeURIComponent(this.state.searchStatus)}`);
            }

            const statements = await V2.get<any[]>(`statements/summary?${parameters.join("&")}`);

            this.setState({
                isLoading: false,

                statements,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load statements"
            });
        }
    }

    private saveSearchState() {
        const currentLocation = this.props.location;
        currentLocation.state = {
            searchOffset: this.state.searchOffset,
            searchStatus: this.state.searchStatus,
            searchText: this.state.searchText,

            sortColumn: this.state.sortColumn,
            sortDirection: this.state.sortDirection
        };
        this.props.history.replace(currentLocation);
    }

    private sortIcon(column: string) {
        return this.state.sortColumn === column
            ? <i className={`icon-chevron-${this.state.sortDirection === "asc" ? "up" : "down"} icon-style-smaller`} />
            : undefined
    }

    private sortByField(column: string) {
        const direction = this.state.sortColumn === column && this.state.sortDirection === "asc" ? "desc" : "asc";

        this.setState({
            sortColumn: column,
            sortDirection: direction
        }, () => this.loadData());
    }

    private statementStatusToText(status: number, canEmail = true) {
        switch (status) {
            case 0: return canEmail ? "Not sent" : "Not printed";
            case 1: return "Sent";
            case 2: return "Print Queued";
            case 3: return "Printed";
            case 4: return "Skipped";
            case 10: return "Payment Authorized";
            case 11: return "Payment Settled";
            case 12: return "Payment Rejected";
            case 13: return "Payment Disputed";
            case 20: return "Payment Posted";
            case 21: return "Voided";
            default: return status;
        }
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.loadData());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.loadData());
    }

    private goAccount(id: number) {
        this.props.history.push(`/accounting/statements/${id}`);
    }

    private openPdf(id: string) {
        V2.get<Blob>(`statements/${id}/pdf`)
            .then(v => {
                const blob = URL.createObjectURL(v);
                const wnd = window.open(blob, `stmt_${id}`, "resizable,scrollbars,status");

                if (wnd) {
                    wnd.addEventListener("unload", e => {
                        if ((e.currentTarget! as Window).location.protocol === "blob:") {
                            URL.revokeObjectURL(blob);
                        }
                    });
                }
            })
            .catch(() => { });
    }
}

export default withTitle("Statements", StatementsListView);